import React, { useState } from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  useTheme,
  Tooltip,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import { toggleOriginError } from '../../api/client';

function OriginErrorCard({ pipeline, isActive, onToggle }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    try {
      setLoading(true);
      await toggleOriginError(pipeline.id);
      if (onToggle) {
        await onToggle();
      }
    } catch (error) {
      console.error('Failed to toggle Origin error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip
      title={
        isActive
          ? 'Click to restore original manifest URLs for origin streams'
          : 'Click to simulate error on origin streams only'
      }
    >
      <Card
        sx={{
          background: isActive
            ? isDark
              ? 'rgba(255, 152, 0, 0.2)'
              : 'rgba(255, 152, 0, 0.1)'
            : isDark
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.02)',
          border: isActive
            ? `2px solid ${isDark ? '#ffb74d' : '#ff9800'}`
            : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: isActive
              ? `0 4px 12px ${isDark ? 'rgba(255,152,0,0.3)' : 'rgba(255,152,0,0.3)'}`
              : `0 4px 12px ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
          },
        }}
      >
        <CardActionArea onClick={handleClick} disabled={loading}>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 1.5,
              px: 1,
              minHeight: 80,
              justifyContent: 'center',
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#ff9800' }} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <StorageIcon
                  sx={{
                    fontSize: 28,
                    color: isActive
                      ? isDark
                        ? '#ffb74d'
                        : '#ff9800'
                      : isDark
                      ? 'rgba(255,255,255,0.3)'
                      : 'rgba(0,0,0,0.26)',
                    animation: isActive ? 'pulse 1.5s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: isActive
                      ? isDark
                        ? '#ffb74d'
                        : '#ff9800'
                      : 'text.secondary',
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}
                >
                  Origin Error
                </Typography>
                {isActive && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.65rem',
                      color: isDark ? '#ffcc80' : '#ffb74d',
                    }}
                  >
                    ACTIVE
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    </Tooltip>
  );
}

export default OriginErrorCard;
